import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export const EventCategorySchema = z.enum([
  'motion-detection',
  'wellness-check',
  'emergency',
]);
export type EventCategory = z.infer<typeof EventCategorySchema>;

export const EventCategory = {
  MOTION: 'motion-detection',
  WELLNESS_CHECK: 'wellness-check',
  EMERGENCY: 'emergency',
} as const satisfies Record<string, EventCategory>;

export const EventPrioritySchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type EventPriority = z.infer<typeof EventPrioritySchema>;

export const EventPriority = {
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
} as const satisfies Record<string, EventPriority>;

// ============================================================================
// PARAM SCHEMAS
// ============================================================================

export const eventIdParam = z.object({ id: z.string().min(1) });
export type EventIdParam = z.infer<typeof eventIdParam>;

// ============================================================================
// QUERY SCHEMAS
// ============================================================================

// `patientIds` may arrive as a comma-separated string or repeated query params
// (Express parses repeated `?patientIds=a&patientIds=b` as an array).
export const eventsForPatientsQuery = z.object({
  patientIds: z
    .union([z.string(), z.array(z.string())])
    .transform((v) => {
      if (Array.isArray(v)) return v.flatMap((s) => s.split(',')).map((s) => s.trim()).filter(Boolean);
      return v.split(',').map((s) => s.trim()).filter(Boolean);
    })
    .pipe(z.array(z.string().min(1)).min(1)),
});
export type EventsForPatientsQuery = z.infer<typeof eventsForPatientsQuery>;

// ============================================================================
// BODY SCHEMAS
// ============================================================================

export const createEventBody = z.object({
  category: EventCategorySchema,
  patientId: z.uuid(),
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }).optional(),
  priority: z.number(),
});
export type CreateEventBody = z.infer<typeof createEventBody>;

export const updateEventBody = z.object({
  category: EventCategorySchema.optional(),
  start: z.iso.datetime({ offset: true }).optional(),
  end: z.iso.datetime({ offset: true }).optional(),
  priority: z.number().optional(),
  completingUserId: z.uuid().optional(),
});
export type UpdateEventBody = z.infer<typeof updateEventBody>;

export const endEventBody = z.object({}).optional();
export type EndEventBody = z.infer<typeof endEventBody>;
